import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  InnerBusinessFormBox,
  TextFieldWrapBusinessForm,
  InputLabel,
} from '../SignUpForms/SignUpForms.styled';
import '../SignUpForms/SignUpForms.css';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { Grid, Button } from '@material-ui/core';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';

const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

const minutesOptions = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0'),
);

const toTotalMinutes = (h, m) => {
  const hh = Number(h);
  const mm = Number(m);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const validateDaySchedule = (schedule) => {
  if (!schedule) return { ok: false, reason: 'missing' };
  if (schedule.close) return { ok: true };

  const { fromHour, fromMinutes, toHour, toMinutes } = schedule;

  if (
    fromHour === '' ||
    fromMinutes === '' ||
    toHour === '' ||
    toMinutes === ''
  ) {
    return { ok: false, reason: 'incomplete' };
  }

  const start = toTotalMinutes(fromHour, fromMinutes);
  const end = toTotalMinutes(toHour, toMinutes);

  if (start === null || end === null)
    return { ok: false, reason: 'invalid_time' };
  if (end <= start) return { ok: false, reason: 'end_before_start' };

  return { ok: true };
};

const getTimePartsByTimezone = (dateString, timezone) => {
  const date = new Date(dateString);

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);

  const hourPart = parts.find((p) => p.type === 'hour');
  const minutePart = parts.find((p) => p.type === 'minute');

  return {
    hour: hourPart ? hourPart.value : '',
    minutes: minutePart ? minutePart.value : '',
  };
};

const parseScheduleFromAPI = (apiSchedule) => {
  if (!apiSchedule) {
    return {
      fromHour: '',
      fromMinutes: '',
      toHour: '',
      toMinutes: '',
      close: false,
    };
  }

  const timezone = apiSchedule.timezone || 'UTC';

  const from = getTimePartsByTimezone(apiSchedule.fromTime, timezone);
  const to = getTimePartsByTimezone(apiSchedule.toTime, timezone);

  return {
    fromHour: from.hour,
    fromMinutes: from.minutes,
    toHour: to.hour,
    toMinutes: to.minutes,
    close: apiSchedule.close || false,
  };
};

const DaySchedule = ({ day, name, schedule, onScheduleChange, hasError }) => {
  const { t } = useTranslation();

  const fromHour = (schedule && schedule.fromHour) || '';
  const fromMinutes = (schedule && schedule.fromMinutes) || '';
  const toHour = (schedule && schedule.toHour) || '';
  const toMinutes = (schedule && schedule.toMinutes) || '';
  const close = (schedule && schedule.close) || false;

  const handleChange = (field, value) => {
    onScheduleChange(day, { ...schedule, [field]: value });
  };

  const rowStyle = hasError
    ? {
        border: '1px solid #d32f2f',
        borderRadius: 8,
        padding: 8,
        background: 'rgba(211,47,47,0.06)',
      }
    : {};

  return (
    <TextFieldWrapBusinessForm style={rowStyle}>
      <Grid container spacing={3} alignItems="center">
        <Grid item className="dayGrid">
          <b style={{ color: hasError ? '#d32f2f' : undefined }}>{name}</b>
        </Grid>

        <Grid item>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <FormControl
              variant="outlined"
              className="categorySelect"
              style={{ width: 'auto' }}>
              <Select
                name={`${day}FromHour`}
                value={fromHour}
                onChange={(e) => handleChange('fromHour', e.target.value)}>
                <MenuItem value="" disabled>
                  <em>{t('BusinessForm.categorySelect.select')}</em>
                </MenuItem>
                {Array.from({ length: 24 }, (_, i) => {
                  const hour = String(i).padStart(2, '0');
                  return (
                    <MenuItem key={hour} value={hour}>
                      {hour}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <b>&nbsp;&nbsp;:&nbsp;&nbsp;</b>

            <FormControl
              variant="outlined"
              className="categorySelect"
              style={{ width: 'auto' }}>
              <Select
                name={`${day}FromMinutes`}
                value={fromMinutes}
                onChange={(e) => handleChange('fromMinutes', e.target.value)}>
                <MenuItem value="" disabled>
                  <em>{t('BusinessForm.categorySelect.select')}</em>
                </MenuItem>
                {minutesOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>

        <Grid item>
          <b>{t('BusinessForm.scheduleForm.to')}</b>
        </Grid>

        <Grid item>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <FormControl
              variant="outlined"
              className="categorySelect"
              style={{ width: 'auto' }}>
              <Select
                name={`${day}ToHour`}
                value={toHour}
                onChange={(e) => handleChange('toHour', e.target.value)}>
                <MenuItem value="" disabled>
                  <em>{t('BusinessForm.categorySelect.select')}</em>
                </MenuItem>
                {Array.from({ length: 24 }, (_, i) => {
                  const hour = String(i).padStart(2, '0');
                  return (
                    <MenuItem key={hour} value={hour}>
                      {hour}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <b>&nbsp;&nbsp;:&nbsp;&nbsp;</b>

            <FormControl
              variant="outlined"
              className="categorySelect"
              style={{ width: 'auto' }}>
              <Select
                name={`${day}ToMinutes`}
                value={toMinutes}
                onChange={(e) => handleChange('toMinutes', e.target.value)}>
                <MenuItem value="" disabled>
                  <em>{t('BusinessForm.categorySelect.select')}</em>
                </MenuItem>
                {minutesOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>

        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                name={`${day}Closed`}
                color="primary"
                checked={close}
                onChange={(e) => handleChange('close', e.target.checked)}
              />
            }
          />
        </Grid>

        {hasError && (
          <Grid item xs={12} style={{ marginTop: -8 }}>
            <small style={{ color: '#d32f2f' }}>
              {t('BusinessForm.scheduleForm.invalidDay')}
            </small>
          </Grid>
        )}
      </Grid>
    </TextFieldWrapBusinessForm>
  );
};

export const ScheduleForm = ({
  schedules,
  onSchedulesChange,
  exposeValidate,
}) => {
  const { t } = useTranslation();

  const days = useMemo(
    () => [
      { day: 'monday', name: t('BusinessForm.scheduleForm.monday') },
      { day: 'tuesday', name: t('BusinessForm.scheduleForm.tuesday') },
      { day: 'wednesday', name: t('BusinessForm.scheduleForm.wednesday') },
      { day: 'thursday', name: t('BusinessForm.scheduleForm.thursday') },
      { day: 'friday', name: t('BusinessForm.scheduleForm.friday') },
      { day: 'saturday', name: t('BusinessForm.scheduleForm.saturday') },
      { day: 'sunday', name: t('BusinessForm.scheduleForm.sunday') },
    ],
    [t],
  );

  const [localSchedules, setLocalSchedules] = useState(() => {
    const initial = {};
    days.forEach(({ day }) => {
      initial[day] = parseScheduleFromAPI(schedules && schedules[day]);
    });
    return initial;
  });

  const [errorsByDay, setErrorsByDay] = useState({});
  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null, // 'open_all' | 'close_all'
    title: '',
    description: '',
  });

  // Cuando llegan schedules del API, sincroniza local
  useEffect(() => {
    if (schedules) {
      const parsed = {};
      days.forEach(({ day }) => {
        parsed[day] = parseScheduleFromAPI(schedules[day]);
      });
      setLocalSchedules(parsed);
      setErrorsByDay({});
      if (onSchedulesChange) onSchedulesChange(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules, days]);

  const handleScheduleChange = (day, newSchedule) => {
    const updated = { ...localSchedules, [day]: newSchedule };
    setLocalSchedules(updated);

    const res = validateDaySchedule(newSchedule);
    setErrorsByDay((prev) => ({ ...prev, [day]: !res.ok }));

    if (onSchedulesChange) onSchedulesChange(updated);
  };

  // validateAll
  const validateAll = useCallback(() => {
    const next = {};
    let hasAny = false;

    days.forEach(({ day }) => {
      const res = validateDaySchedule(localSchedules[day]);
      next[day] = !res.ok;
      if (!res.ok) hasAny = true;
    });

    setErrorsByDay(next);

    if (hasAny) {
      setSnack({
        open: true,
        severity: 'error',
        message: t('BusinessForm.scheduleForm.validationError'),
      });
    }

    return !hasAny;
  }, [days, localSchedules, t]);

  // Guardamos validateAll en ref para que el padre llame siempre a la versión más nueva
  const validateAllRef = useRef(() => true);
  useEffect(() => {
    validateAllRef.current = validateAll;
  }, [validateAll]);

  // Exponer SOLO una vez (depende solo de exposeValidate)
  useEffect(() => {
    if (typeof exposeValidate === 'function') {
      exposeValidate(() => validateAllRef.current());
    }
  }, [exposeValidate]);

  const allClosed = Object.values(localSchedules).every(
    (s) => s && s.close === true,
  );

  const openConfirm = (action) => {
    if (action === 'open_all') {
      setConfirmDialog({
        open: true,
        action,
        title: 'Open store',
        description:
          'This will mark every day as "open". Do you want to continue?',
      });
    } else {
      setConfirmDialog({
        open: true,
        action,
        title: 'Close store',
        description:
          'This will mark every day as "closed". Do you want to continue?',
      });
    }
  };

  const applyOpenAll = () => {
    const updated = { ...localSchedules };
    days.forEach(({ day }) => {
      updated[day] = { ...updated[day], close: false };
    });
    setLocalSchedules(updated);

    const nextErrors = {};
    days.forEach(({ day }) => {
      nextErrors[day] = !validateDaySchedule(updated[day]).ok;
    });
    setErrorsByDay(nextErrors);

    if (onSchedulesChange) onSchedulesChange(updated);

    setSnack({
      open: true,
      severity: 'success',
      message: t('BusinessForm.scheduleForm.snack.openSuccess'),
    });
  };

  const applyCloseAll = () => {
    const updated = { ...localSchedules };
    days.forEach(({ day }) => {
      updated[day] = { ...updated[day], close: true };
    });
    setLocalSchedules(updated);

    const nextErrors = {};
    days.forEach(({ day }) => {
      nextErrors[day] = false;
    });
    setErrorsByDay(nextErrors);

    if (onSchedulesChange) onSchedulesChange(updated);

    setSnack({
      open: true,
      severity: 'success',
      message: t('BusinessForm.scheduleForm.snack.closeSuccess'),
    });
  };

  return (
    <>
      {/* Botones superiores: NO hay Save aquí */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
          gap: 8,
        }}>
        <Button
          variant="outlined"
          color="default"
          onClick={() => openConfirm('open_all')}>
          {t('Schedules.open')}
        </Button>

        <Button
          variant="contained"
          onClick={() => openConfirm('close_all')}
          style={{
            backgroundColor: allClosed ? '#CC545A' : '#616161',
            color: 'white',
          }}>
          {t('Schedules.close')}
        </Button>
      </div>

      <InnerBusinessFormBox>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <InputLabel style={{ marginRight: '344px' }}>
            {t('BusinessForm.scheduleForm.setYourSchedule')}
          </InputLabel>
          <InputLabel>{t('BusinessForm.scheduleForm.close')}</InputLabel>
        </div>

        {days.map(({ day, name }) => (
          <DaySchedule
            key={day}
            day={day}
            name={name}
            schedule={localSchedules[day]}
            onScheduleChange={handleScheduleChange}
            hasError={Boolean(errorsByDay[day])}
          />
        ))}
      </InnerBusinessFormBox>

      {/* Dialog confirm abrir/cerrar */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((s) => ({ ...s, open: false }))}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <div>{confirmDialog.description}</div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog((s) => ({ ...s, open: false }))}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              const action = confirmDialog.action;
              setConfirmDialog((s) => ({ ...s, open: false }));
              if (action === 'open_all') applyOpenAll();
              if (action === 'close_all') applyCloseAll();
            }}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar interno (validación + abrir/cerrar) */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};
